import React, { useEffect, useState, useCallback } from 'react';
import { getOverview, getLowStock, getExpiring } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import '../App.css';

export default function OverviewPage({ labId, labName }) {
  const [overview, setOverview]   = useState(null);
  const [lowStock, setLowStock]   = useState([]);
  const [expiring, setExpiring]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const loadData = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    return Promise.all([getOverview(labId), getLowStock(labId), getExpiring(30, labId)])
      .then(([o, l, e]) => {
        setOverview(o.data);
        setLowStock(l.data.slice(0, 8));
        setExpiring(e.data.slice(0, 8));
        setLastUpdated(new Date());
      })
      .finally(() => { setLoading(false); setRefreshing(false); });
  }, [labId]);

  useEffect(() => {
    loadData();
    const timer = setInterval(() => loadData(true), 60000);
    return () => clearInterval(timer);
  }, [loadData]);

  if (loading) return <div className="loading">Loading…</div>;

  const o = overview || {};

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Overview</div>
          <div className="page-sub">HLA Lab Inventory Status · {new Date().toDateString()}{labName ? ` · ${labName}` : ''}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {lastUpdated && (
            <span style={{ fontSize: 12, color: '#94a3b8' }}>
              Updated {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button
            className="btn btn-outline btn-sm"
            onClick={() => loadData(true)}
            disabled={refreshing}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <span style={{ display: 'inline-block', animation: refreshing ? 'spin 1s linear infinite' : 'none' }}>↻</span>
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="stat-grid">
        <div className="stat-card">
          <div className="stat-val">{o.total_items ?? 0}</div>
          <div className="stat-lbl">Total Items</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '4px solid #dc2626' }}>
          <div className="stat-val" style={{ color: '#dc2626' }}>{o.critical_count ?? 0}</div>
          <div className="stat-lbl">Critical Stock</div>
          <div className="stat-sub">Below hard threshold</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '4px solid #d97706' }}>
          <div className="stat-val" style={{ color: '#d97706' }}>{o.low_count ?? 0}</div>
          <div className="stat-lbl">Low Stock</div>
          <div className="stat-sub">Below soft threshold</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '4px solid #b45309' }}>
          <div className="stat-val" style={{ color: '#b45309' }}>{o.expiring_soon ?? 0}</div>
          <div className="stat-lbl">Expiring Soon</div>
          <div className="stat-sub">Within 30 days</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '4px solid #16a34a' }}>
          <div className="stat-val" style={{ color: '#16a34a' }}>{o.ok_count ?? 0}</div>
          <div className="stat-lbl">Items OK</div>
        </div>
        <div className="stat-card" style={{ borderLeft: '4px solid #7c3aed' }}>
          <div className="stat-val" style={{ color: o.unlinked_count > 0 ? '#7c3aed' : '#16a34a' }}>{o.unlinked_count ?? 0}</div>
          <div className="stat-lbl">No Barcode Yet</div>
          <div className="stat-sub">{o.unlinked_count > 0 ? 'Link during first audit' : 'All items linked ✓'}</div>
        </div>
      </div>

      <div className="overview-grid">
        {/* By section bar chart */}
        {o.by_section?.length > 0 && (
          <div className="card">
            <div className="card-title">Stock Status by Section</div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={o.by_section} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="critical" name="Critical" fill="#dc2626" stackId="a" />
                <Bar dataKey="low"      name="Low"      fill="#d97706" stackId="a" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Critical / low stock list */}
        {lowStock.length > 0 && (
          <div className="card">
            <div className="card-title">⚠️ Low Stock Items</div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Item</th><th>Section</th><th>Qty</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {lowStock.map(item => {
                    const isCrit = item.hard_threshold !== null && item.current_qty <= item.hard_threshold;
                    return (
                      <tr key={item.id}>
                        <td>{item.name}</td>
                        <td>{item.section_name}</td>
                        <td><b>{item.current_qty}</b> {item.unit}</td>
                        <td>
                          <span className={`badge ${isCrit ? 'badge-critical' : 'badge-low'}`}>
                            {isCrit ? '🔴 Critical' : '🟡 Low'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Expiring soon */}
      {expiring.length > 0 && (
        <div className="card">
          <div className="card-title">📅 Expiring Within 30 Days</div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Item</th><th>Lot</th><th>Expires</th><th>Section</th><th>Qty</th></tr>
              </thead>
              <tbody>
                {expiring.map((lot, i) => {
                  const daysLeft = Math.ceil((new Date(lot.expiry_date) - new Date()) / 86400000);
                  return (
                    <tr key={i}>
                      <td>{lot.item_name}</td>
                      <td><code>{lot.lot_number || '—'}</code></td>
                      <td style={{ color: daysLeft <= 7 ? '#dc2626' : '#d97706' }}>
                        {lot.expiry_date?.slice(0,10)} ({daysLeft}d)
                      </td>
                      <td>{lot.section_name}</td>
                      <td>{lot.qty}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
